/**
 * Provincial / territorial boundaries layer (Stage 5 §5.6).
 *
 * Decorative context only: 1px white 30%-opacity strokes, no fill, no labels,
 * non-interactive. Always-on at country + regional zoom; tiered out below
 * ~street level via a distanceDisplayCondition so the lines don't clutter a
 * close-in city view.
 *
 * Source: Natural Earth (public domain) Canadian province/territory polygons
 * (via CartoDB), simplified offline to ~50 KB and shipped at
 * `apps/web/public/geo/ca-provinces.geojson`. The spec called for StatsCan
 * CC-BY data; because the boundaries are purely decorative, a public-domain
 * Natural Earth derivative is a clean drop-in and avoids the StatsCan shapefile
 * → GeoJSON conversion. Swapping in the StatsCan file later is a file replace
 * with no code change (same FeatureCollection-of-(Multi)Polygon shape).
 *
 * The MapView owns the Cesium viewer, so this module exposes an imperative
 * installer rather than a React component: MapView calls it inside the same
 * effect that builds the viewer and disposes it on unmount.
 */
import * as Cesium from 'cesium';
import { COUNTRY_VISIBLE_FAR_M } from '../coneVisibility';

/** White at 30% opacity (spec). */
export const BOUNDARY_COLOR = Cesium.Color.WHITE.withAlpha(0.3);
export const BOUNDARY_WIDTH_PX = 1;
/** Below this eye distance (≈ street/city zoom) the boundaries fade out — the
 *  near bound of the DDC, so lines show only when the eye is farther than this. */
export const BOUNDARY_MIN_EYE_M = 60_000;
/** Effectively unbounded far side — always visible from regional out to country. */
export const BOUNDARY_MAX_EYE_M = COUNTRY_VISIBLE_FAR_M;

interface GeoJsonPolygonish {
  properties?: { name?: string } | null;
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: number[][][] | number[][][][];
  };
}
interface BoundaryFeatureCollection {
  features: GeoJsonPolygonish[];
}

/**
 * Pure helper (unit-testable without Cesium): flatten every polygon ring in the
 * FeatureCollection to a `[lon, lat, lon, lat, …]` array — the shape Cesium's
 * `Cartesian3.fromDegreesArray` consumes. One entry per ring (outer + holes).
 */
export function flattenBoundaryRings(fc: BoundaryFeatureCollection): number[][] {
  const rings: number[][] = [];
  for (const f of fc.features ?? []) {
    const g = f.geometry;
    if (!g) continue;
    const polys = (
      g.type === 'MultiPolygon' ? g.coordinates : [g.coordinates]
    ) as number[][][][];
    for (const poly of polys) {
      for (const ring of poly) {
        const flat: number[] = [];
        for (const pt of ring) {
          if (pt.length >= 2) flat.push(pt[0] as number, pt[1] as number);
        }
        if (flat.length >= 6) rings.push(flat); // ≥3 points
      }
    }
  }
  return rings;
}

export interface BoundaryLayerHandle {
  /** Show/hide the boundary polylines (Context-group toggle, L.1). */
  setVisible(visible: boolean): void;
  destroy(): void;
}

/**
 * Fetch + render the provincial boundary polylines into `viewer`. Resolves once
 * the entities are added. Returns a handle whose `destroy()` removes them.
 * Aborting `opts.signal` (e.g. MapView unmount mid-fetch) rejects/no-ops safely.
 */
export async function installProvincialBoundaries(
  viewer: Cesium.Viewer,
  opts: { url?: string; signal?: AbortSignal } = {},
): Promise<BoundaryLayerHandle> {
  const url = opts.url ?? '/geo/ca-provinces.geojson';
  const res = await fetch(url, opts.signal ? { signal: opts.signal } : undefined);
  if (!res.ok) throw new Error(`provincial boundaries fetch failed: ${res.status}`);
  const fc = (await res.json()) as BoundaryFeatureCollection;
  // The fetch is async, so the viewer may have been torn down meanwhile (React
  // StrictMode's throwaway mount in dev, or a fast unmount). Touching
  // viewer.entities on a destroyed viewer throws — bail to a no-op handle.
  if (viewer.isDestroyed()) {
    return { setVisible: () => undefined, destroy: () => undefined };
  }
  const rings = flattenBoundaryRings(fc);

  const ddc = new Cesium.DistanceDisplayCondition(BOUNDARY_MIN_EYE_M, BOUNDARY_MAX_EYE_M);
  const material = new Cesium.ColorMaterialProperty(BOUNDARY_COLOR);
  const added: Cesium.Entity[] = [];
  viewer.entities.suspendEvents();
  rings.forEach((flat, i) => {
    const entity = viewer.entities.add({
      // `province:` id → MapView's click handler treats it as a non-selectable
      // (decorative) pick, so clicking a boundary clears selection like the
      // globe does, never opening a panel.
      id: `province:${i}`,
      polyline: {
        positions: Cesium.Cartesian3.fromDegreesArray(flat),
        width: BOUNDARY_WIDTH_PX,
        material,
        clampToGround: true,
        distanceDisplayCondition: ddc,
      },
    });
    added.push(entity);
  });
  viewer.entities.resumeEvents();

  return {
    setVisible(visible: boolean): void {
      if (viewer.isDestroyed()) return;
      for (const e of added) e.show = visible;
    },
    destroy(): void {
      if (viewer.isDestroyed()) return;
      for (const e of added) viewer.entities.remove(e);
    },
  };
}
